/**
 * A conversa recarregada não pode aparecer DUAS VEZES.
 *
 * MEDIDO no develop implantado (chat `f1af3ee3`, motor `developer` `7f8e0b1`,
 * 10/09/2026), num turno real contra o motor:
 *
 *   | medição                                  | "olá" | bolhas | botões de card |
 *   | ---------------------------------------- | ----- | ------ | -------------- |
 *   | ao vivo                                  |   1   |   1    |       4        |
 *   | recarga na MESMA aba                     | **2** | **2**  |    **8**       |
 *   | carga fria, contexto novo sem localStorage |  1   |   1    |       4        |
 *
 * E o servidor está certo: `GET /api/v1/conversations/{id}` devolveu
 * `messages.length === 2` nas duas cargas. A duplicação é do CHAT, e some
 * quando o `localStorage` está vazio — que é a assinatura do defeito.
 *
 * POR QUE ACONTECE. Desde `29219952` (05/09/2026, "o cache do disco ganha teto
 * e o historico volta a ser buscado") o `ChatPage` busca o histórico SEMPRE, e
 * o comentário que autorizou isso diz: _"`mergeMessages` ja descarta por id,
 * entao rebuscar nao duplica nada"_. A premissa é falsa. Os ids do turno VIVO
 * são gerados no NAVEGADOR — `engine-${Date.now()}` para o assistente
 * (`connectionStore.ts`, caminho da resposta única) e `crypto.randomUUID()`
 * para a bolha do usuário —, enquanto o histórico chega com o id do BANCO
 * (medido: `40fe7442-…` e `df76e652-…`). Dois ids diferentes para o mesmo
 * turno, então o `Set` de ids do `mergeMessages` não casa nada e as duas
 * cópias sobrevivem. O turno que a pessoa acabou de viver é exatamente o que
 * duplica.
 *
 * A REGRA, e ela não depende de id: dentro de UMA conversa, uma mensagem local
 * e uma do servidor com o MESMO papel e o MESMO texto são o mesmo turno. Quando
 * há gêmeo, quem fica é a cópia do SERVIDOR — ela traz o id canônico e o
 * envelope persistido (`suggestedActions`, `richContent`, `metadata`), já
 * passado pelo `rehydratePresentation`. O que não tem gêmeo no servidor
 * continua na tela: é o turno em voo, o balão de erro e o card que nasceu só no
 * cliente (o resumo de importação, que o motor nunca persistiu).
 *
 * Casamento por MULTICONJUNTO, não por conjunto: quem mandou "olá" duas vezes
 * tem dois turnos, e dois gêmeos são consumidos — não um só, que apagaria uma
 * mensagem de verdade.
 *
 * PURO: sem I/O, sem React, sem store.
 */

/** O mínimo que a regra precisa saber de uma mensagem. */
export interface MensagemMesclavel {
  id: string
  conversationId: string
  role: 'user' | 'assistant'
  content: string
  /**
   * Epoch ms do turno. Opcional porque os testes e o caminho sem histórico não
   * carregam relógio; quando existe, é o que coloca a mensagem só-do-cliente na
   * posição cronológica em vez de sempre no fim (STO-1467 — o card de
   * importação precisa cair ANTES do aviso terminal do motor).
   */
  timestamp?: number
  /** Presentation supplied by a proven local card click; never a wire instruction. */
  displayText?: string
}

/** Keep proven user-click presentation while canonical server data wins. */
function preservarApresentacaoLocal<T extends MensagemMesclavel>(local: T, servidor: T): T {
  if (
    local.role !== 'user' ||
    servidor.role !== 'user' ||
    local.content !== servidor.content ||
    !local.displayText ||
    servidor.displayText !== undefined
  )
    return servidor
  return { ...servidor, displayText: local.displayText }
}

/** A identidade de um turno dentro de uma conversa: papel + texto. */
function identidadeDoTurno(m: MensagemMesclavel): string {
  return `${m.role}::${m.content}`
}

/**
 * Funde o histórico do servidor na lista que já está na tela, sem duplicar o
 * turno que a pessoa acabou de viver.
 *
 * A ORDEM importa de verdade — `MessageList` renderiza na ordem do array e é o
 * ÚLTIMO turno do assistente que fica dono dos botões. Por isso o bloco do
 * SERVIDOR entra INTEIRO e na ordem dele, no lugar onde a conversa começava na
 * tela. Substituir cada local no próprio lugar embaralharia a conversa quando a
 * poda do cache em disco (`TETO_DO_CACHE_NO_DISCO`) deixou só as últimas
 * mensagens: as antigas do servidor cairiam DEPOIS das recentes.
 *
 * O que só existe no cliente entra na POSIÇÃO CRONOLÓGICA dele (`timestamp`),
 * nunca sempre no fim. MEDIDO (STO-1467, 23/09/2026): o card de importação
 * (`import_job_summary`) nasce no cliente lá no enfileiramento e o aviso
 * terminal é do MOTOR, persistido depois; jogado para o fim, o card caía
 * DEPOIS do aviso e a tela mostrava "Importação concluída" ACIMA do card. Sem
 * relógio (`timestamp` ausente) — turno em voo, balão de erro, teste — mantém
 * o fim, que é a ordem segura.
 *
 * @param locais         o que está na tela agora (todas as conversas).
 * @param doServidor     o histórico desta conversa, já reidratado, em ordem.
 * @param conversationId a conversa que o histórico descreve.
 */
export function mesclarHistoricoDoServidor<T extends MensagemMesclavel>(
  locais: readonly T[],
  doServidor: readonly T[],
  conversationId: string,
): T[] {
  if (doServidor.length === 0) return [...locais]

  // Fila de índices do servidor por identidade de turno: casa o mais antigo
  // primeiro, que é a ordem em que turnos de mesmo texto aconteceram.
  const filaPorIdentidade = new Map<string, number[]>()
  const indicePorId = new Map<string, number>()
  doServidor.forEach((m, i) => {
    const k = identidadeDoTurno(m)
    const fila = filaPorIdentidade.get(k)
    if (fila) fila.push(i)
    else filaPorIdentidade.set(k, [i])
    indicePorId.set(m.id, i)
  })

  const casados = new Set<number>()
  const corpo: T[] = [...doServidor]

  /** O índice do gêmeo desta mensagem local, ou -1. Id primeiro, texto depois. */
  function acharGemeo(local: T): number {
    const porId = indicePorId.get(local.id)
    if (porId !== undefined && !casados.has(porId)) return porId
    const fila = filaPorIdentidade.get(identidadeDoTurno(local))
    if (!fila) return -1
    for (const i of fila) if (!casados.has(i)) return i
    return -1
  }

  const antes: T[] = []
  const soDoCliente: T[] = []
  let achouAConversa = false
  for (const local of locais) {
    // Mensagem de OUTRA conversa (ou sem conversa — o card avulso que a lista
    // mostra em qualquer thread) não é assunto deste histórico.
    if (local.conversationId !== conversationId) {
      ;(achouAConversa ? soDoCliente : antes).push(local)
      continue
    }
    achouAConversa = true
    // Com gêmeo no servidor, quem fica é a cópia do servidor — id canônico e
    // envelope persistido. Sem gêmeo, é turno em voo / balão de erro / card que
    // nasceu só no cliente, e continua na tela.
    const i = acharGemeo(local)
    if (i < 0) soDoCliente.push(local)
    else {
      casados.add(i)
      corpo[i] = preservarApresentacaoLocal(local, doServidor[i]!)
    }
  }

  for (const soDoClienteItem of soDoCliente) {
    const ts = soDoClienteItem.timestamp
    // Sem relógio não há como posicionar: fica no fim, junto do turno em voo.
    if (ts === undefined) {
      corpo.push(soDoClienteItem)
      continue
    }
    let idx = corpo.length
    for (let i = 0; i < corpo.length; i += 1) {
      const st = corpo[i]?.timestamp
      if (st !== undefined && st > ts) {
        idx = i
        break
      }
    }
    corpo.splice(idx, 0, soDoClienteItem)
  }

  return [...antes, ...corpo]
}
